import { useId, type InputHTMLAttributes, type ReactNode } from 'react';

import { useTranslations } from '@/i18n/client';

export interface FilePickerProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'className' | 'id' | 'type'
> {
  label: string;
  buttonText?: string;
  hint?: ReactNode;
  selectedFiles?: readonly File[];
  selectedFilesLabel?: ReactNode;
  trailingAction?: ReactNode;
  id?: string;
  allowDirectory?: boolean;
}

export function FilePicker({
  label,
  buttonText,
  hint,
  selectedFiles = [],
  selectedFilesLabel,
  trailingAction,
  id,
  allowDirectory = false,
  multiple,
  ...inputProps
}: FilePickerProps) {
  const t = useTranslations('common.filePicker');
  const generatedId = useId();
  const inputId = id ?? `file-picker-${generatedId}`;
  const labelId = `${inputId}-label`;
  const selectedFilesTitle = selectedFiles.map((file) => file.name).join('\n');
  const statusText =
    selectedFiles.length === 0
      ? t('noFileChosen')
      : !multiple && selectedFiles[0]
        ? selectedFiles[0].name
        : (selectedFilesLabel ?? t('selectedFiles', { count: selectedFiles.length }));

  return (
    <div className="md-field rounded-md border border-[var(--md-border)] bg-[var(--md-bg-soft)] p-3 transition-colors hover:border-[var(--md-primary)] hover:bg-[var(--md-primary-light)] focus-within:border-[var(--md-primary)] focus-within:bg-[var(--md-primary-light)]">
      <label className="md-field__label text-[var(--md-primary)]" id={labelId} htmlFor={inputId}>
        {label}
      </label>
      <input
        {...inputProps}
        {...(allowDirectory ? ({ webkitdirectory: '', directory: '' } as Record<string, string>) : {})}
        id={inputId}
        className="peer sr-only"
        type="file"
        multiple={multiple}
        aria-labelledby={labelId}
      />
      <div className="flex min-w-0 items-stretch gap-2">
        <label
          className="flex min-h-11 min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-md border border-[var(--md-border)] bg-[var(--md-white)] p-2 peer-focus-visible:outline-2 peer-focus-visible:outline-[var(--zs-focus)] peer-focus-visible:outline-offset-1 peer-disabled:cursor-not-allowed peer-disabled:opacity-60"
          htmlFor={inputId}
          aria-hidden="true"
        >
          <span className="inline-flex min-h-9 shrink-0 items-center rounded-md bg-[var(--zs-accent)] px-3 py-2 font-bold text-[var(--zs-accent-contrast)]">
            {buttonText ?? t('chooseFile')}
          </span>
          <span
            className="min-w-0 truncate text-sm text-[var(--md-text)]"
            title={selectedFilesTitle || undefined}
          >
            {statusText}
          </span>
        </label>
        {trailingAction}
      </div>
      {hint !== undefined && hint !== null && hint !== '' ? (
        <small className="text-xs text-[var(--zs-muted)]">{hint}</small>
      ) : null}
    </div>
  );
}
